import React from 'react'
import { api } from '../lib/api'
import { useNavigate } from 'react-router-dom'

function openDaumPostcode(onComplete) {
  if (!window?.daum?.Postcode) {
    throw new Error('daum_postcode_not_loaded')
  }
  new window.daum.Postcode({
    oncomplete: function (data) {
      onComplete && onComplete(data)
    },
  }).open()
}

function pick(obj, keys) {
  for (const k of keys) {
    const v = obj?.[k]
    if (v === null || v === undefined) continue
    const s = String(v).trim()
    if (s) return s
  }
  return null
}

function formatDateDigits(v) {
  if (!v) return null
  const s = String(v).replace(/[^\d]/g, '')
  if (s.length === 8) return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`
  if (s.length === 6) return `${s.slice(0, 4)}-${s.slice(4, 6)}`
  return String(v)
}

function formatNumber(v) {
  if (v === null || v === undefined) return null
  const n = Number(String(v).replace(/,/g, '').trim())
  if (!Number.isFinite(n)) return String(v)
  return n.toLocaleString('ko-KR')
}

function parseMoneyInput(v) {
  if (v === null || v === undefined) return null
  const s = String(v).replace(/[^\d]/g, '')
  if (!s) return null
  const n = Number(s)
  if (!Number.isFinite(n)) return null
  return Math.max(0, Math.floor(n))
}

function formatMoney(n) {
  if (n === null || n === undefined) return ''
  const v = Number(n)
  if (!Number.isFinite(v)) return ''
  return v.toLocaleString('ko-KR')
}

function numericPart(v) {
  const s = String(v || '').replace(/[^\d]/g, '')
  if (!s) return null
  const n = Number(s)
  return Number.isFinite(n) ? n : null
}

function isBasementFloorName(v) {
  const s = String(v || '').trim()
  if (!s) return false
  if (s.includes('지하')) return true
  if (s.startsWith('지')) return true // 예: 지1, 지2
  if (s.toUpperCase().startsWith('B')) return true // 예: B1
  return false
}

function floorOrderName(flrNoNm) {
  const s = String(flrNoNm || '').trim()
  if (!s) return 999999
  const n = numericPart(s)
  if (!Number.isFinite(n)) return 999999
  return isBasementFloorName(s) ? -n : n
}

function compareFloorName(a, b) {
  const oa = floorOrderName(a)
  const ob = floorOrderName(b)
  if (oa !== ob) return oa - ob
  return String(a || '').localeCompare(String(b || ''), 'ko-KR')
}

function compareDongHo(a, b) {
  const na = numericPart(a)
  const nb = numericPart(b)
  if (Number.isFinite(na) && Number.isFinite(nb) && na !== nb) return na - nb
  return String(a || '').localeCompare(String(b || ''), 'ko-KR')
}

function Modal({ open, title, onClose, headerRight, children }) {
  React.useEffect(() => {
    if (!open) return
    function onEsc(e) {
      if (e.key === 'Escape') onClose && onClose()
    }
    window.addEventListener('keydown', onEsc)
    return () => window.removeEventListener('keydown', onEsc)
  }, [open, onClose])

  if (!open) return null
  return (
    <div
      className="fixed inset-0 z-[1000] bg-black/40 p-4 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose && onClose()
      }}
    >
      <div className="w-full max-w-5xl rounded-xl bg-white shadow-xl border">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div className="font-semibold text-slate-900">{title || '상세'}</div>
          <div className="flex items-center gap-2">
            {headerRight || null}
            <button
              type="button"
              className="rounded border px-3 py-1.5 text-sm hover:bg-slate-50"
              onClick={() => onClose && onClose()}
            >
              닫기
            </button>
          </div>
        </div>
        <div className="p-4 max-h-[76vh] overflow-auto">
          {children}
        </div>
      </div>
    </div>
  )
}

function BuildingCompactTable({ title, obj }) {
  const items = React.useMemo(() => {
    const defs = [
      { label: '건물명', keys: ['bldNm'], span: 3 },
      { label: '주용도', keys: ['mainPurpsCdNm', 'mainPurpsCd'] },
      { label: '구조', keys: ['strctCdNm', 'strctCd'] },
      { label: '사용승인(준공)', keys: ['useAprDay', 'pmsDay', 'useAprDt'], format: formatDateDigits },
      { label: '연면적', keys: ['totArea'], format: (v) => `${formatNumber(v)} ㎡` },
      { label: '건축면적', keys: ['archArea'], format: (v) => `${formatNumber(v)} ㎡` },
      { label: '건폐율', keys: ['bcRat'], format: (v) => `${v}%` },
      { label: '용적률', keys: ['vlRat'], format: (v) => `${v}%` },
      { label: '지상층수', keys: ['grndFlrCnt', 'grndFlrCo', 'grndFlr'] },
      { label: '지하층수', keys: ['ugrndFlrCnt', 'ugrndFlrCo', 'ugrndFlr'] },
      { label: '세대수', keys: ['hhldCnt', 'hhldCo'] },
      { label: '가구수', keys: ['fmlyCnt', 'fmlyCo'] },
      { label: '내진설계', keys: ['rserthqkDsgnApplyYn'] },
      { label: '높이', keys: ['heit'], format: (v) => `${formatNumber(v)} m` },
      // 대지위치는 총괄개요에만 표시(표제부에는 중복이라 숨김)
      ...(title === '총괄개요' ? [
        { label: '대지위치(지번)', keys: ['platPlc', 'platPlcNm'], span: 3 },
        { label: '대지위치(도로명)', keys: ['newPlatPlc', 'newPlatPlcNm'], span: 3 },
      ] : []),
    ]

    const out = []
    for (const d of defs) {
      const raw = pick(obj, d.keys)
      if (!raw) continue
      const value = d.format ? d.format(raw) : raw
      if (!value) continue
      out.push({ label: d.label, value, span: d.span || 1 })
    }
    return out
  }, [obj])

  return (
    <div className="rounded border bg-white">
      <div className="px-3 py-2 border-b text-sm font-semibold text-slate-900">{title}</div>
      {items.length === 0 ? (
        <div className="px-3 py-3 text-sm text-slate-500">표시할 상세 항목이 없습니다.</div>
      ) : (
        <div className="p-3 grid grid-cols-1 md:grid-cols-3 gap-2">
          {items.map((it) => (
            <div
              key={it.label}
              className={[
                'rounded border bg-slate-50 px-3 py-2',
                it.span === 3 ? 'md:col-span-3' : it.span === 2 ? 'md:col-span-2' : '',
              ].join(' ').trim()}
            >
              <div className="text-[11px] font-semibold text-slate-500">{it.label}</div>
              <div className="mt-0.5 text-sm text-slate-900 break-words">{it.value}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function AmountTable({ amounts }) {
  const a = amounts || {}
  const rows = [
    { label: '건물', value: a.building },
    { label: '가재', value: a.household },
    { label: '집기', value: a.contents },
    { label: '시설', value: a.facility },
    { label: '동산', value: a.movables },
    { label: '기계', value: a.machinery },
  ]
  const total = rows.reduce((s, r) => s + (typeof r.value === 'number' ? r.value : 0), 0)
  return (
    <div className="rounded border bg-white">
      <div className="px-3 py-2 border-b text-sm font-semibold text-slate-900">보험가액(입력, 만원)</div>
      <div className="p-3">
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
          {rows.map((r) => (
            <div key={r.label} className="rounded border bg-slate-50 px-3 py-2 text-sm">
              <div className="text-[11px] font-semibold text-slate-500">{r.label}</div>
              <div className="mt-0.5 font-medium text-slate-900 break-words">
                {typeof r.value === 'number' ? `${formatMoney(r.value)} 만원` : '-'}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-2 rounded border bg-white px-3 py-2 text-sm flex items-center justify-between gap-3">
          <div className="text-slate-700 font-semibold">합계</div>
          <div className="text-slate-900 font-semibold break-words">
            {total > 0 ? `${formatMoney(total)} 만원` : '-'}
          </div>
        </div>
      </div>
    </div>
  )
}

function UnitAreaTable({ dongNm, hoNm, rows }) {
  const d = String(dongNm || '').trim()
  const h = String(hoNm || '').trim()
  const list = Array.isArray(rows) ? rows : []
  const sums = React.useMemo(() => {
    const out = { 전유: 0, 공용: 0 }
    for (const r of list) {
      const gb = String(r?.exposPubuseGbCdNm || '').trim()
      const area = Number(String(r?.area || '').replace(/,/g, '').trim())
      if (!Number.isFinite(area)) continue
      if (gb.includes('전유')) out.전유 += area
      else out.공용 += area
    }
    return out
  }, [list])
  return (
    <div className="rounded border bg-white">
      <div className="px-3 py-2 border-b text-sm font-semibold text-slate-900">전유부(선택)</div>
      {!d || !h ? (
        <div className="px-3 py-3 text-sm text-slate-600 bg-slate-50">
          동/호를 선택 후 <span className="font-semibold text-slate-900">동/호 적용</span>을 누르면 전유/공용면적(전유부) 상세가 표시됩니다.
        </div>
      ) : (
        <>
          <div className="px-3 py-2 text-sm text-slate-700 border-b bg-slate-50 flex flex-wrap items-center justify-between gap-2">
            <div>동/호: <span className="font-semibold text-slate-900">{d} / {h}</span></div>
            <div className="text-xs text-slate-600">
              전유 합계: <span className="font-semibold text-slate-900">{sums.전유 ? sums.전유.toFixed(3) : '-'}</span> ㎡
              <span className="mx-2">|</span>
              공용 합계: <span className="font-semibold text-slate-900">{sums.공용 ? sums.공용.toFixed(3) : '-'}</span> ㎡
            </div>
          </div>
          {list.length === 0 ? (
            <div className="px-3 py-3 text-sm text-slate-500">전유/공용면적 정보가 없습니다.</div>
          ) : (
            <div className="overflow-auto">
              <table className="w-full text-sm">
                <thead className="bg-white text-slate-600">
                  <tr className="border-b">
                    <th className="text-left px-3 py-2 whitespace-nowrap">구분</th>
                    <th className="text-left px-3 py-2 whitespace-nowrap">층</th>
                    <th className="text-left px-3 py-2 whitespace-nowrap">층구분</th>
                    <th className="text-left px-3 py-2">용도</th>
                    <th className="text-left px-3 py-2 whitespace-nowrap">구조</th>
                    <th className="text-right px-3 py-2 whitespace-nowrap">면적(㎡)</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {list.map((r, idx) => (
                    <tr key={`${r?.exposPubuseGbCdNm || 'row'}-${idx}`} className="text-slate-900">
                      <td className="px-3 py-2 whitespace-nowrap">{r?.exposPubuseGbCdNm || '-'}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{r?.flrNoNm || '-'}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{r?.flrGbCdNm || '-'}</td>
                      <td className="px-3 py-2">{r?.purpsNm || r?.mainPurpsCdNm || '-'}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{r?.strctCdNm || '-'}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-right">{r?.area ? formatNumber(r.area) : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  )
}

function FloorTable({ rows, selectedMode, selectedFloorNoNm, onSelectMode, onSelectFloor }) {
  const mode = selectedMode || 'ALL'
  const selected = String(selectedFloorNoNm || '').trim()
  const [open, setOpen] = React.useState(false)
  const [detailOpen, setDetailOpen] = React.useState(false)

  function floorOrder(flrNoNm) {
    return floorOrderName(flrNoNm)
  }

  const sorted = React.useMemo(() => {
    const list = Array.isArray(rows) ? rows : []
    const copy = [...list]
    copy.sort((a, b) => {
      const da = String(a?.dongNm || '').trim()
      const db = String(b?.dongNm || '').trim()
      if (da || db) {
        const c = compareDongHo(da, db)
        if (c !== 0) return c
      }
      const oa = floorOrder(a?.flrNoNm)
      const ob = floorOrder(b?.flrNoNm)
      if (oa !== ob) return oa - ob
      return String(a?.flrNoNm || '').localeCompare(String(b?.flrNoNm || ''), 'ko-KR')
    })
    return copy
  }, [rows])

  const floorOptions = React.useMemo(() => {
    const set = new Set()
    for (const r of sorted) {
      const v = String(r?.flrNoNm || '').trim()
      if (v) set.add(v)
    }
    const arr = Array.from(set)
    arr.sort(compareFloorName)
    return arr
  }, [sorted])

  const filtered = React.useMemo(() => {
    if (mode === 'ONE' && selected) {
      return sorted.filter((r) => String(r?.flrNoNm || '').trim() === selected)
    }
    return sorted
  }, [sorted, mode, selected])

  const floorSummary = React.useMemo(() => {
    // 선택된 층(또는 전체)에 대해 용도/구조/층구분 기준으로 면적을 합산해서 요약
    const map = new Map()
    for (const r of filtered) {
      const key = [
        String(r?.dongNm || '').trim(),
        String(r?.flrNoNm || '').trim(),
        String(r?.flrGbCdNm || '').trim(),
        String(r?.mainPurpsCdNm || '').trim(),
        String(r?.strctCdNm || '').trim(),
        String(r?.mainAtchGbCdNm || '').trim(),
        String(r?.areaExctYn || '').trim(),
      ].join('|')
      const area = Number(String(r?.area || '').replace(/,/g, '').trim())
      const prev = map.get(key) || { count: 0, areaSum: 0, r }
      prev.count += 1
      if (Number.isFinite(area)) prev.areaSum += area
      map.set(key, prev)
    }
    const arr = Array.from(map.values()).map((v) => ({
      dongNm: v.r?.dongNm,
      flrNoNm: v.r?.flrNoNm,
      flrGbCdNm: v.r?.flrGbCdNm,
      mainPurpsCdNm: v.r?.mainPurpsCdNm,
      strctCdNm: v.r?.strctCdNm,
      mainAtchGbCdNm: v.r?.mainAtchGbCdNm,
      areaExctYn: v.r?.areaExctYn,
      count: v.count,
      areaSum: v.areaSum,
    }))
    arr.sort((a, b) => {
      const c = compareDongHo(String(a?.dongNm || '').trim(), String(b?.dongNm || '').trim())
      if (c !== 0) return c
      return floorOrder(a.flrNoNm) - floorOrder(b.flrNoNm)
    })
    return arr
  }, [filtered])

  const view = React.useMemo(() => {
    if (mode === 'ONE' && selected) return filtered
    if (open) return filtered
    return filtered.slice(0, 30)
  }, [filtered, mode, selected, open])

  return (
    <div className="rounded border bg-white">
      <div className="px-3 py-2 border-b text-sm font-semibold text-slate-900 flex items-center justify-between gap-2">
        <span>층별개요</span>
        {mode !== 'ONE' && filtered.length > 30 && (
          <button
            type="button"
            className="text-xs rounded border px-2 py-1 hover:bg-slate-50"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? '접기' : `펼치기(${filtered.length}건)`}
          </button>
        )}
      </div>

      <div className="px-3 py-2 border-b bg-slate-50">
        <div className="flex flex-wrap items-center gap-3 text-sm text-slate-700">
          <label className="inline-flex items-center gap-2">
            <input
              type="radio"
              name="floorModeInTable"
              checked={mode === 'ALL'}
              onChange={() => {
                onSelectMode && onSelectMode('ALL')
                onSelectFloor && onSelectFloor('')
                setDetailOpen(false)
              }}
            />
            <span>전체층</span>
          </label>
          <label className="inline-flex items-center gap-2">
            <input
              type="radio"
              name="floorModeInTable"
              checked={mode === 'ONE'}
              onChange={() => {
                onSelectMode && onSelectMode('ONE')
                setDetailOpen(true)
              }}
            />
            <span>특정층</span>
          </label>
          <div className="min-w-[220px]">
            <select
              className="w-full rounded-md border px-3 py-2 text-sm disabled:bg-slate-50"
              value={selected}
              onChange={(e) => {
                onSelectMode && onSelectMode('ONE')
                onSelectFloor && onSelectFloor(e.target.value)
                setDetailOpen(true)
              }}
              disabled={floorOptions.length === 0}
            >
              <option value="">{floorOptions.length ? '층 선택' : '층 없음'}</option>
              {floorOptions.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>
          <button
            type="button"
            className="ml-auto text-xs rounded border px-2 py-1 hover:bg-white"
            onClick={() => setDetailOpen((v) => !v)}
          >
            {detailOpen ? '상세 접기' : '상세 보기'}
          </button>
        </div>
        <div className="mt-1 text-[11px] text-slate-500">
          * 선택된 층(또는 전체)에 대해 용도/구조 기준으로 합산 요약을 먼저 보여줍니다.
        </div>
      </div>

      {floorSummary.length > 0 && (
        <div className="overflow-auto border-b">
          <table className="w-full text-sm">
            <thead className="bg-white text-slate-600">
              <tr className="border-b">
                <th className="text-left px-3 py-2 whitespace-nowrap">동</th>
                <th className="text-left px-3 py-2 whitespace-nowrap">층</th>
                <th className="text-left px-3 py-2 whitespace-nowrap">층구분</th>
                <th className="text-left px-3 py-2 whitespace-nowrap">용도</th>
                <th className="text-left px-3 py-2 whitespace-nowrap">구조</th>
                <th className="text-left px-3 py-2 whitespace-nowrap">주/부속</th>
                <th className="text-left px-3 py-2 whitespace-nowrap">면적제외</th>
                <th className="text-right px-3 py-2 whitespace-nowrap">면적합(㎡)</th>
                <th className="text-right px-3 py-2 whitespace-nowrap">건수</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {floorSummary.slice(0, 40).map((r, idx) => (
                <tr key={`${r.flrNoNm || 'flr'}-${idx}`} className="text-slate-900">
                  <td className="px-3 py-2 whitespace-nowrap">{r.dongNm || '-'}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{r.flrNoNm || '-'}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{r.flrGbCdNm || '-'}</td>
                  <td className="px-3 py-2">{r.mainPurpsCdNm || '-'}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{r.strctCdNm || '-'}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{r.mainAtchGbCdNm || '-'}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{String(r.areaExctYn || '').trim() === '1' ? '제외' : '-'}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-right">{r.areaSum ? r.areaSum.toFixed(3) : '-'}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-right">{r.count}</td>
                </tr>
              ))}
              {floorSummary.length > 40 && (
                <tr>
                  <td className="px-3 py-2 text-xs text-slate-500" colSpan={9}>
                    요약이 너무 많아 {floorSummary.length}건 중 40건만 표시했습니다.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {!detailOpen ? (
        <div className="px-3 py-3 text-sm text-slate-500">상세 보기를 누르면 원본 행을 확인할 수 있습니다.</div>
      ) : (
        view.length === 0 ? (
          <div className="px-3 py-3 text-sm text-slate-500">층별개요 정보가 없습니다.</div>
        ) : (
          <div className="overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-700">
                <tr>
                  <th className="text-left px-3 py-2 whitespace-nowrap">동</th>
                  <th className="text-left px-3 py-2 whitespace-nowrap">층</th>
                  <th className="text-left px-3 py-2 whitespace-nowrap">층구분</th>
                  <th className="text-left px-3 py-2 whitespace-nowrap">용도</th>
                  <th className="text-left px-3 py-2 whitespace-nowrap">구조</th>
                  <th className="text-left px-3 py-2 whitespace-nowrap">주/부속</th>
                  <th className="text-left px-3 py-2 whitespace-nowrap">면적제외</th>
                  <th className="text-right px-3 py-2 whitespace-nowrap">면적(㎡)</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {view.map((r, idx) => (
                  <tr key={`${r?.flrNoNm || 'flr'}-${idx}`} className="text-slate-900">
                    <td className="px-3 py-2 whitespace-nowrap">{r?.dongNm || '-'}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{r?.flrNoNm || '-'}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{r?.flrGbCdNm || '-'}</td>
                    <td className="px-3 py-2">{r?.mainPurpsCdNm || '-'}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{r?.strctCdNm || '-'}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{r?.mainAtchGbCdNm || '-'}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{String(r?.areaExctYn || '').trim() === '1' ? '제외' : '-'}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-right">{r?.area ? formatNumber(r.area) : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  )
}

function HousingPriceTable({ rows }) {
  const list = Array.isArray(rows) ? rows : []
  return (
    <div className="rounded border bg-white">
      <div className="px-3 py-2 border-b text-sm font-semibold text-slate-900">주택가격(건축물대장, 참고)</div>
      {list.length === 0 ? (
        <div className="px-3 py-3 text-sm text-slate-500">주택가격 정보가 없습니다.</div>
      ) : (
        <div className="divide-y">
          {list.slice(0, 5).map((r, idx) => (
            <div key={`${r?.stdDay || 'std'}-${idx}`} className="flex items-start gap-3 px-3 py-2 text-sm">
              <div className="w-28 shrink-0 text-slate-500">
                <div className="text-[11px] font-semibold">기준일</div>
                <div className="mt-0.5">{formatDateDigits(r?.stdDay) || '-'}</div>
              </div>
              <div className="flex-1 text-slate-900 break-words">
                {r?.hsprc ? `${formatNumber(r.hsprc)} 원` : '-'}
                {r?.bylotCnt ? <span className="ml-2 text-xs text-slate-500">(부속지번수 {r.bylotCnt})</span> : null}
                {r?.crtnDay ? <div className="mt-0.5 text-[11px] text-slate-500">자료생성일: {formatDateDigits(r.crtnDay) || '-'}</div> : null}
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="px-3 py-2 text-[11px] text-slate-500 border-t bg-slate-50">
        * 공시/평가 성격의 참고값이며, 보험가액(복구비용)을 직접 대체하지 않습니다.
        <span className="ml-2">* 기준일과 자료생성일은 서로 다를 수 있습니다.</span>
      </div>
    </div>
  )
}

function AtchJibunTable({ rows }) {
  const list = Array.isArray(rows) ? rows : []
  return (
    <div className="rounded border bg-white">
      <div className="px-3 py-2 border-b text-sm font-semibold text-slate-900">부속지번</div>
      {list.length === 0 ? (
        <div className="px-3 py-3 text-sm text-slate-500">부속지번 정보가 없습니다.</div>
      ) : (
        <div className="overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-700">
              <tr>
                <th className="text-left px-3 py-2 whitespace-nowrap">구분</th>
                <th className="text-left px-3 py-2 whitespace-nowrap">대지구분</th>
                <th className="text-left px-3 py-2 whitespace-nowrap">번</th>
                <th className="text-left px-3 py-2 whitespace-nowrap">지</th>
                <th className="text-left px-3 py-2 whitespace-nowrap">기타지번명</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {list.map((r, idx) => (
                <tr key={`${r?.atchBun || 'atch'}-${idx}`} className="text-slate-900">
                  <td className="px-3 py-2 whitespace-nowrap">{r?.atchRegstrGbCdNm || '-'}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{r?.atchPlatGbCd || '-'}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{r?.atchBun || '-'}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{r?.atchJi || '-'}</td>
                  <td className="px-3 py-2">{r?.atchEtcJibunNm || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function JijiguTable({ rows }) {
  const [open, setOpen] = React.useState(false)
  const raw = Array.isArray(rows) ? rows : []
  const list = React.useMemo(() => {
    const seen = new Set()
    const out = []
    for (const r of raw) {
      const k = [
        String(r?.jijiguGbCdNm || '').trim(),
        String(r?.jijiguCdNm || '').trim(),
        String(r?.reprYn || '').trim(),
        String(r?.etcJijigu || '').trim(),
      ].join('|')
      if (seen.has(k)) continue
      seen.add(k)
      out.push(r)
    }
    return out
  }, [raw])
  return (
    <div className="rounded border bg-white">
      <div className="px-3 py-2 border-b text-sm font-semibold text-slate-900 flex items-center justify-between gap-2">
        <span>지역지구구역</span>
        {list.length > 12 && (
          <button
            type="button"
            className="text-xs rounded border px-2 py-1 hover:bg-slate-50"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? '접기' : `펼치기(${list.length}건)`}
          </button>
        )}
      </div>
      <div className="px-3 py-2 text-[11px] text-slate-500 border-b bg-slate-50">
        * 지역지구구역은 건축물에 적용되는 지정(용도지역/지구/구역 등)이 여러 개 겹칠 수 있어 다건으로 내려옵니다.
      </div>
      {list.length === 0 ? (
        <div className="px-3 py-3 text-sm text-slate-500">지역지구구역 정보가 없습니다.</div>
      ) : (
        <div className="overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-700">
              <tr>
                <th className="text-left px-3 py-2 whitespace-nowrap">구분</th>
                <th className="text-left px-3 py-2 whitespace-nowrap">코드명</th>
                <th className="text-left px-3 py-2 whitespace-nowrap">대표</th>
                <th className="text-left px-3 py-2 whitespace-nowrap">비고</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {(open ? list : list.slice(0, 12)).map((r, idx) => (
                <tr key={`${r?.jijiguCdNm || 'jijigu'}-${idx}`} className="text-slate-900">
                  <td className="px-3 py-2 whitespace-nowrap">{r?.jijiguGbCdNm || '-'}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{r?.jijiguCdNm || '-'}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{r?.reprYn || '-'}</td>
                  <td className="px-3 py-2">{r?.etcJijigu || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default function FireBuildingRegistryPage() {
  const navigate = useNavigate()
  const [addr, setAddr] = React.useState(null)
  const [loading, setLoading] = React.useState(false)
  const [result, setResult] = React.useState(null)
  const [error, setError] = React.useState(null)
  const [detailOpen, setDetailOpen] = React.useState(false)
  const [designOpen, setDesignOpen] = React.useState(false)
  const [customerName, setCustomerName] = React.useState('')
  const [commonExistingCustomer, setCommonExistingCustomer] = React.useState(false)
  const [amtBuilding, setAmtBuilding] = React.useState('')
  const [amtHousehold, setAmtHousehold] = React.useState('')
  const [amtContents, setAmtContents] = React.useState('')
  const [amtFacility, setAmtFacility] = React.useState('')
  const [amtMovables, setAmtMovables] = React.useState('')
  const [amtMachinery, setAmtMachinery] = React.useState('')
  const [selectedDongNm, setSelectedDongNm] = React.useState('')
  const [selectedHoNm, setSelectedHoNm] = React.useState('')
  const [floorSelectMode, setFloorSelectMode] = React.useState('ALL') // ALL | ONE
  const [selectedFloorNoNm, setSelectedFloorNoNm] = React.useState('')

  const roadAddress = addr?.roadAddress || ''
  const jibunAddress = addr?.jibunAddress || ''
  const bcode = addr?.bcode || '' // 법정동코드(10자리) - JUSO 없이 조회할 때 핵심

  async function onLookup() {
    setLoading(true)
    setError(null)
    setResult(null)
    try {
      const res = await api.post('/fire/building-registry/lookup', {
        roadAddress: roadAddress || null,
        jibunAddress: jibunAddress || null,
        bcode: bcode || null,
        dongNm: selectedDongNm || null,
        hoNm: selectedHoNm || null,
      })
      const data = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      setResult(data)
      if (!data?.ok) {
        setError(data?.message || '조회 실패')
      }
    } catch (e) {
      const serverMsg =
        e?.response?.data?.message
        || e?.response?.data?.error
        || e?.response?.data?.detail
        || e?.response?.data?.msg
      setError(serverMsg || e?.message || '조회 실패')
    } finally {
      setLoading(false)
    }
  }

  // 주소를 다시 선택하면 전유부 선택은 초기화
  React.useEffect(() => {
    setSelectedDongNm('')
    setSelectedHoNm('')
    setFloorSelectMode('ALL')
    setSelectedFloorNoNm('')
  }, [roadAddress, jibunAddress, bcode])

  // 동/호 적용(전유부 선택) 후: 전유부 응답의 층 정보를 이용해 '선택 층'을 자동 세팅
  React.useEffect(() => {
    if (!result?.ok) return
    const d = String(result?.selectedDongNm || '').trim()
    const h = String(result?.selectedHoNm || '').trim()
    if (!d || !h) return

    const rows = Array.isArray(result?.unitAreaRows) ? result.unitAreaRows : []
    if (rows.length === 0) return

    // 전유 행의 층을 우선 사용(없으면 첫 유효 층)
    const pickFloor = () => {
      const prefer = rows.find((r) => String(r?.exposPubuseGbCdNm || '').includes('전유') && String(r?.flrNoNm || '').trim())
      if (prefer) return String(prefer.flrNoNm || '').trim()
      const any = rows.find((r) => String(r?.flrNoNm || '').trim())
      return any ? String(any.flrNoNm || '').trim() : ''
    }

    const flr = pickFloor()
    if (!flr) return
    // "각층" 같은 값은 선택층으로 부적합
    if (flr.includes('각층')) return

    // 사용자가 이미 특정층을 선택해두지 않았다면 자동 세팅
    const already = String(selectedFloorNoNm || '').trim()
    if (floorSelectMode === 'ONE' && already) return

    setFloorSelectMode('ONE')
    setSelectedFloorNoNm(flr)
  }, [result, floorSelectMode, selectedFloorNoNm])

  const unitOptions = React.useMemo(() => {
    return Array.isArray(result?.unitOptions) ? result.unitOptions : []
  }, [result?.unitOptions])

  const floorRows = React.useMemo(() => {
    return Array.isArray(result?.floorRows) ? result.floorRows : []
  }, [result?.floorRows])
  const housingPriceRows = React.useMemo(() => {
    return Array.isArray(result?.housingPriceRows) ? result.housingPriceRows : []
  }, [result?.housingPriceRows])
  const atchJibunRows = React.useMemo(() => {
    return Array.isArray(result?.atchJibunRows) ? result.atchJibunRows : []
  }, [result?.atchJibunRows])
  const jijiguRows = React.useMemo(() => {
    return Array.isArray(result?.jijiguRows) ? result.jijiguRows : []
  }, [result?.jijiguRows])

  const floorOptions = React.useMemo(() => {
    const set = new Set()
    for (const r of floorRows) {
      const v = String(r?.flrNoNm || '').trim()
      if (v) set.add(v)
    }
    return Array.from(set).sort(compareFloorName)
  }, [floorRows])
  const dongOptions = React.useMemo(() => {
    const set = new Set()
    for (const u of unitOptions) {
      const d = String(u?.dongNm || '').trim()
      if (d) set.add(d)
    }
    return Array.from(set).sort(compareDongHo)
  }, [unitOptions])
  const hoOptions = React.useMemo(() => {
    const d = String(selectedDongNm || '').trim()
    if (!d) return []
    const set = new Set()
    for (const u of unitOptions) {
      if (String(u?.dongNm || '').trim() !== d) continue
      const h = String(u?.hoNm || '').trim()
      if (h) set.add(h)
    }
    return Array.from(set).sort(compareDongHo)
  }, [unitOptions, selectedDongNm])

  const titleItem = result?.titleItem || null
  const basisItem = result?.basisItem || null

  const summary = React.useMemo(() => {
    const purps = pick(titleItem, ['mainPurpsCdNm', 'mainPurpsCd', 'etcPurps', 'etcPurpsNm'])
      || pick(basisItem, ['mainPurpsCdNm', 'mainPurpsCd', 'etcPurps', 'etcPurpsNm'])
    const strct = pick(titleItem, ['strctCdNm', 'strctCd'])
      || pick(basisItem, ['strctCdNm', 'strctCd'])
    const useApr = formatDateDigits(pick(titleItem, ['useAprDay', 'pmsDay', 'useAprDt']))
      || formatDateDigits(pick(basisItem, ['useAprDay', 'pmsDay', 'useAprDt']))
    const totArea = pick(basisItem, ['totArea', 'totarea', 'totar'])
      || pick(titleItem, ['totArea', 'totarea', 'totar'])
    const grnd = pick(basisItem, ['grndFlrCnt', 'grndFlrCo', 'grndFlr'])
      || pick(titleItem, ['grndFlrCnt', 'grndFlrCo', 'grndFlr'])
    const ugrnd = pick(basisItem, ['ugrndFlrCnt', 'ugrndFlrCo', 'ugrndFlr'])
      || pick(titleItem, ['ugrndFlrCnt', 'ugrndFlrCo', 'ugrndFlr'])
    const bldNm = pick(titleItem, ['bldNm', 'bldNmNm'])
      || pick(basisItem, ['bldNm'])
    return { bldNm, purps, strct, useApr, totArea, grnd, ugrnd }
  }, [titleItem, basisItem])

  function buildPrefill() {
    const cleanName = String(customerName || '').trim()
    const title = `${cleanName} 고객님 화재보험 설계 요청드립니다.`
    const amounts = {
      building: parseMoneyInput(amtBuilding),
      household: parseMoneyInput(amtHousehold),
      contents: parseMoneyInput(amtContents),
      facility: parseMoneyInput(amtFacility),
      movables: parseMoneyInput(amtMovables),
      machinery: parseMoneyInput(amtMachinery),
    }
    const total = Object.values(amounts).reduce((s, v) => s + (typeof v === 'number' ? v : 0), 0)

    const floorMemoLines = (() => {
      if (!result?.ok) return []
      if (!Array.isArray(floorRows) || floorRows.length === 0) return []
      const mode = floorSelectMode === 'ONE' ? 'ONE' : 'ALL'
      const selected = String(selectedFloorNoNm || '').trim()
      const target = (mode === 'ONE' && selected)
        ? floorRows.filter((r) => String(r?.flrNoNm || '').trim() === selected)
        : floorRows
      const cap = 60
      const list = target.slice(0, cap)
      return [
        '',
        `[층별개요(${mode === 'ONE' ? '특정층 선택' : '전체층'})]`,
        ...(mode === 'ONE' && selected ? [`- 선택층: ${selected}`] : []),
        ...list.map((r) => {
          const flr = String(r?.flrNoNm || '').trim() || '-'
          const gb = String(r?.flrGbCdNm || '').trim()
          const purp = String(r?.mainPurpsCdNm || '').trim()
          const area = r?.area ? `${formatNumber(r.area)} ㎡` : '-'
          const strct = String(r?.strctCdNm || '').trim()
          const parts = [
            `- ${flr}`,
            gb ? `(${gb})` : null,
            purp ? `용도:${purp}` : null,
            strct ? `구조:${strct}` : null,
            `면적:${area}`,
          ].filter(Boolean)
          return parts.join(' / ')
        }),
        ...(target.length > cap ? [`- ... 외 ${target.length - cap}건 생략`] : []),
      ]
    })()

    const hsprcMemoLines = (() => {
      if (!result?.ok) return []
      if (!Array.isArray(housingPriceRows) || housingPriceRows.length === 0) return []
      const top = housingPriceRows[0]
      return [
        '',
        '[주택가격(건축물대장, 참고)]',
        `- 기준일: ${formatDateDigits(top?.stdDay) || '-'}`,
        `- 주택가격: ${top?.hsprc ? `${formatNumber(top.hsprc)} 원` : '-'}`,
      ]
    })()

    const lines = [
      `- 도로명주소: ${roadAddress || '-'}`,
      `- 지번주소: ${jibunAddress || '-'}`,
      `- 법정동코드(bcode): ${bcode || '-'}`,
      ...(result?.selectedDongNm && result?.selectedHoNm ? [
        '',
        '[전유부(선택)]',
        `- 동/호: ${result.selectedDongNm} / ${result.selectedHoNm}`,
        ...(() => {
          const rows = Array.isArray(result?.unitAreaRows) ? result.unitAreaRows : []
          if (rows.length === 0) return ['- 전유/공용면적: -']

          const cap = 40
          const list = rows.slice(0, cap)
          const lines = list.map((r) => {
            const gb = String(r?.exposPubuseGbCdNm || '').trim() || '-'
            const flr = String(r?.flrNoNm || '').trim()
            const flrGb = String(r?.flrGbCdNm || '').trim()
            const purp = String(r?.purpsNm || r?.mainPurpsCdNm || '').trim()
            const strct = String(r?.strctCdNm || '').trim()
            const area = r?.area ? `${formatNumber(r.area)} ㎡` : '-'
            const parts = [
              `- ${gb}`,
              flr ? `층:${flr}` : null,
              flrGb ? `(${flrGb})` : null,
              purp ? `용도:${purp}` : null,
              strct ? `구조:${strct}` : null,
              `면적:${area}`,
            ].filter(Boolean)
            return parts.join(' / ')
          })

          const sum = (() => {
            let ex = 0
            let pub = 0
            for (const r of rows) {
              const gb = String(r?.exposPubuseGbCdNm || '').trim()
              const n = Number(String(r?.area || '').replace(/,/g, '').trim())
              if (!Number.isFinite(n)) continue
              if (gb.includes('전유')) ex += n
              else if (gb.includes('공용')) pub += n
            }
            return { ex, pub }
          })()

          return [
            `- 전유 합계: ${sum.ex ? sum.ex.toFixed(3) : '-'} ㎡`,
            `- 공용 합계: ${sum.pub ? sum.pub.toFixed(3) : '-'} ㎡`,
            ...lines,
            ...(rows.length > cap ? [`- ... 외 ${rows.length - cap}건 생략`] : []),
          ]
        })(),
      ] : []),
      ...floorMemoLines,
      ...hsprcMemoLines,
      '',
      '[보험가액(입력, 만원)]',
      `- 건물: ${amounts.building != null ? `${formatMoney(amounts.building)} 만원` : '-'}`,
      `- 가재: ${amounts.household != null ? `${formatMoney(amounts.household)} 만원` : '-'}`,
      `- 집기: ${amounts.contents != null ? `${formatMoney(amounts.contents)} 만원` : '-'}`,
      `- 시설: ${amounts.facility != null ? `${formatMoney(amounts.facility)} 만원` : '-'}`,
      `- 동산: ${amounts.movables != null ? `${formatMoney(amounts.movables)} 만원` : '-'}`,
      `- 기계: ${amounts.machinery != null ? `${formatMoney(amounts.machinery)} 만원` : '-'}`,
      `- 합계: ${total > 0 ? `${formatMoney(total)} 만원` : '-'}`,
      '',
      '[건축물대장 요약]',
      `- 건물명: ${summary?.bldNm || '-'}`,
      `- 주용도: ${summary?.purps || '-'}`,
      `- 구조: ${summary?.strct || '-'}`,
      `- 연면적: ${summary?.totArea ? `${formatNumber(summary.totArea)} ㎡` : '-'}`,
      `- 층수: ${(() => {
        const g = summary?.grnd ? `${summary.grnd}층` : null
        const u = summary?.ugrnd ? `지하 ${summary.ugrnd}층` : null
        return [u, g].filter(Boolean).join(' / ') || '-'
      })()}`,
      `- 사용승인: ${summary?.useApr || '-'}`,
    ]
    return {
      source: 'FIRE',
      customerName: cleanName,
      title,
      commonNote: lines.join('\n'),
      commonExistingCustomer: !!commonExistingCustomer,
      fireAmounts: amounts,
    }
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-8">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">건축물대장 조회(주소 기반)</h1>
          {/* <p className="mt-1 text-sm text-slate-600">
            주소를 선택하면 서버에서(카카오 bcode+지번 기반으로) 건축물대장 API를 호출합니다. (JUSO는 추후 옵션)
          </p> */}
        </div>
      </div>

      <div className="mt-6 rounded-xl border bg-white p-4">
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="inline-flex items-center rounded-md bg-brand-navy px-3 py-2 text-sm font-medium text-white hover:opacity-90"
              onClick={() => {
                try {
                  openDaumPostcode((data) => {
                    setAddr(data || null)
                    setResult(null)
                    setError(null)
                    setSelectedDongNm('')
                    setSelectedHoNm('')
                  })
                } catch (e) {
                  setError('주소검색 스크립트 로드에 실패했습니다.')
                }
              }}
            >
              주소 검색
            </button>

            <button
              type="button"
              className="inline-flex items-center rounded-md border px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              disabled={loading || (!roadAddress && !jibunAddress)}
              onClick={onLookup}
            >
              {loading ? '조회 중...' : '건축물대장 조회'}
            </button>
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <div className="text-xs font-semibold text-slate-500">도로명주소</div>
              <div className="mt-1 rounded-md border bg-slate-50 px-3 py-2 text-sm text-slate-800">
                {roadAddress || '-'}
              </div>
            </div>
            <div>
              <div className="text-xs font-semibold text-slate-500">지번주소</div>
              <div className="mt-1 rounded-md border bg-slate-50 px-3 py-2 text-sm text-slate-800">
                {jibunAddress || '-'}
              </div>
            </div>
            <div>
              <div className="text-xs font-semibold text-slate-500">고객명</div>
              <input
                className="mt-1 w-full rounded-md border px-3 py-2 text-sm"
                placeholder="예) 홍길동"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
              />
              <div className="mt-1 text-[11px] text-slate-500">
                * 설계의뢰 생성 시 자동으로 포함됩니다.
              </div>
            </div>
            <div className="flex items-end">
              <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={commonExistingCustomer}
                  onChange={(e) => setCommonExistingCustomer(e.target.checked)}
                />
                <span>기존고객</span>
              </label>
            </div>
            <div className="md:col-span-2">
              <div className="text-xs font-semibold text-slate-500">보험가액(입력, 만원)</div>
              <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-4">
                {[
                  { key: 'building', label: '건물', value: amtBuilding, set: setAmtBuilding },
                  { key: 'household', label: '가재', value: amtHousehold, set: setAmtHousehold },
                  { key: 'contents', label: '집기', value: amtContents, set: setAmtContents },
                  { key: 'facility', label: '시설', value: amtFacility, set: setAmtFacility },
                  { key: 'movables', label: '동산', value: amtMovables, set: setAmtMovables },
                  { key: 'machinery', label: '기계', value: amtMachinery, set: setAmtMachinery },
                ].map((f) => (
                  <div key={f.key}>
                    <div className="text-[11px] font-semibold text-slate-500">{f.label}</div>
                    <input
                      className="mt-1 w-full rounded-md border px-3 py-2 text-sm"
                      inputMode="numeric"
                      placeholder="0 (만원)"
                      value={f.value}
                      onChange={(e) => {
                        const raw = e.target.value
                        const n = parseMoneyInput(raw)
                        f.set(n === null ? '' : formatMoney(n))
                      }}
                    />
                  </div>
                ))}
                <div className="md:col-span-2">
                  <div className="text-[11px] font-semibold text-slate-500">입력합계</div>
                  <div className="mt-1 w-full rounded-md border bg-slate-50 px-3 py-2 text-sm flex items-center justify-between gap-2">
                    <div className="text-slate-700 font-semibold">합계</div>
                    <div className="text-slate-900 font-semibold">
                      {(() => {
                        const vals = [
                          parseMoneyInput(amtBuilding),
                          parseMoneyInput(amtHousehold),
                          parseMoneyInput(amtContents),
                          parseMoneyInput(amtFacility),
                          parseMoneyInput(amtMovables),
                          parseMoneyInput(amtMachinery),
                        ].filter((v) => typeof v === 'number')
                        const sum = vals.reduce((s, v) => s + v, 0)
                        return sum > 0 ? `${formatMoney(sum)} 만원` : '-'
                      })()}
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-2 text-[11px] text-slate-500">
                * 숫자만 입력됩니다. (만원 단위 / 자동 콤마)
              </div>
            </div>
            {result?.ok && (
              <div className="md:col-span-2">
                <div className="text-xs font-semibold text-slate-500">동/호 선택(전유부)</div>
                <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-3">
                  {unitOptions.length > 0 ? (
                    <>
                      <div>
                        <div className="text-[11px] font-semibold text-slate-500">동</div>
                        <select
                          className="mt-1 w-full rounded-md border px-3 py-2 text-sm"
                          value={selectedDongNm}
                          onChange={(e) => {
                            const v = e.target.value
                            setSelectedDongNm(v)
                            setSelectedHoNm('')
                          }}
                        >
                          <option value="">동 선택</option>
                          {dongOptions.map((d) => <option key={d} value={d}>{d}</option>)}
                        </select>
                      </div>
                      <div>
                        <div className="text-[11px] font-semibold text-slate-500">호</div>
                        <select
                          className="mt-1 w-full rounded-md border px-3 py-2 text-sm"
                          value={selectedHoNm}
                          onChange={(e) => setSelectedHoNm(e.target.value)}
                          disabled={!selectedDongNm}
                        >
                          <option value="">{selectedDongNm ? '호 선택' : '동을 먼저 선택'}</option>
                          {hoOptions.map((h) => <option key={h} value={h}>{h}</option>)}
                        </select>
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <div className="text-[11px] font-semibold text-slate-500">동(수동입력)</div>
                        <input
                          className="mt-1 w-full rounded-md border px-3 py-2 text-sm"
                          placeholder="예) 101동"
                          value={selectedDongNm}
                          onChange={(e) => {
                            setSelectedDongNm(e.target.value)
                            // 호 입력은 유지
                          }}
                        />
                      </div>
                      <div>
                        <div className="text-[11px] font-semibold text-slate-500">호(수동입력)</div>
                        <input
                          className="mt-1 w-full rounded-md border px-3 py-2 text-sm"
                          placeholder="예) 1203호"
                          value={selectedHoNm}
                          onChange={(e) => setSelectedHoNm(e.target.value)}
                        />
                      </div>
                    </>
                  )}
                  <div className="flex items-end">
                    <button
                      type="button"
                      className="w-full rounded-md border px-3 py-2 text-sm hover:bg-slate-50 disabled:opacity-50"
                      disabled={!selectedDongNm || !selectedHoNm || loading}
                      onClick={onLookup}
                    >
                      동/호 적용
                    </button>
                  </div>
                </div>
                <div className="mt-1 text-[11px] text-slate-500">
                  {unitOptions.length > 0
                    ? `* 전유부 목록 ${unitOptions.length}건. 동/호를 선택하면 전유공용면적(전유부) 정보를 추가 조회합니다.`
                    : '* 전유부 목록이 없어서 수동 입력으로만 적용됩니다. (일부 주소/대장 유형은 전유부 목록이 비어 있을 수 있어요)'}
                </div>
              </div>
            )}
            {result?.ok && (
              <div className="md:col-span-2">
                <div className="text-xs font-semibold text-slate-500">층 선택(설계 메모 포함)</div>
                {floorRows.length === 0 ? (
                  <div className="mt-2 rounded-md border bg-slate-50 px-3 py-2 text-sm text-slate-600">
                    층별개요 정보가 없습니다.
                  </div>
                ) : (
                  <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-3">
                    <div className="md:col-span-1">
                      <div className="flex items-center gap-3 text-sm text-slate-700">
                        <label className="inline-flex items-center gap-2">
                          <input
                            type="radio"
                            name="floorMode"
                            checked={floorSelectMode === 'ALL'}
                            onChange={() => {
                              setFloorSelectMode('ALL')
                              setSelectedFloorNoNm('')
                            }}
                          />
                          <span>전체층</span>
                        </label>
                        <label className="inline-flex items-center gap-2">
                          <input
                            type="radio"
                            name="floorMode"
                            checked={floorSelectMode === 'ONE'}
                            onChange={() => setFloorSelectMode('ONE')}
                          />
                          <span>특정층</span>
                        </label>
                      </div>
                      <div className="mt-1 text-[11px] text-slate-500">
                        * 설계의뢰 메모에 층별개요가 포함됩니다.
                      </div>
                    </div>
                    <div className="md:col-span-2">
                      <div className="text-[11px] font-semibold text-slate-500">선택 층</div>
                      <select
                        className="mt-1 w-full rounded-md border px-3 py-2 text-sm disabled:bg-slate-50"
                        value={selectedFloorNoNm}
                        onChange={(e) => setSelectedFloorNoNm(e.target.value)}
                        disabled={floorSelectMode !== 'ONE'}
                      >
                        <option value="">{floorSelectMode === 'ONE' ? '층 선택' : '전체층 선택됨'}</option>
                        {floorOptions.map((f) => <option key={f} value={f}>{f}</option>)}
                      </select>
                    </div>
                  </div>
                )}
              </div>
            )}
            <div className="md:col-span-2">
              {/* <div className="text-xs font-semibold text-slate-500">법정동코드(bcode)</div>
              <div className="mt-1 rounded-md border bg-slate-50 px-3 py-2 text-sm text-slate-800">
                {bcode || '-'}
              </div> */}
              {/* {!bcode && (
                <div className="mt-1 text-xs text-slate-500">
                  bcode가 없으면 서버가 JUSO로 정규화해야 합니다(현재는 JUSO 미사용 전제이므로 실패할 수 있어요).
                </div>
              )} */}
            </div>
          </div>

          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}
        </div>
      </div>

      {result && (
        <div className="mt-6 space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="rounded border px-3 py-2 text-sm hover:bg-slate-50"
              onClick={() => setDetailOpen(true)}
            >
              상세 보기
            </button>
            {result?.ok && (
              <button
                type="button"
                className="rounded bg-brand-navy text-white px-3 py-2 text-sm hover:opacity-90"
                onClick={() => {
                  setDesignOpen(true)
                }}
              >
                설계의뢰
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div className="rounded-xl border bg-white p-4">
              <div className="text-xs font-semibold text-slate-500">건물명</div>
              <div className="mt-1 text-sm font-medium text-slate-900">{summary?.bldNm || '-'}</div>
            </div>
            <div className="rounded-xl border bg-white p-4">
              <div className="text-xs font-semibold text-slate-500">주용도</div>
              <div className="mt-1 text-sm font-medium text-slate-900">{summary?.purps || '-'}</div>
            </div>
            <div className="rounded-xl border bg-white p-4">
              <div className="text-xs font-semibold text-slate-500">구조</div>
              <div className="mt-1 text-sm font-medium text-slate-900">{summary?.strct || '-'}</div>
            </div>
            <div className="rounded-xl border bg-white p-4">
              <div className="text-xs font-semibold text-slate-500">사용승인(준공)</div>
              <div className="mt-1 text-sm font-medium text-slate-900">{summary?.useApr || '-'}</div>
            </div>
            <div className="rounded-xl border bg-white p-4">
              <div className="text-xs font-semibold text-slate-500">연면적</div>
              <div className="mt-1 text-sm font-medium text-slate-900">
                {summary?.totArea ? `${formatNumber(summary.totArea)} ㎡` : '-'}
              </div>
            </div>
            <div className="rounded-xl border bg-white p-4">
              <div className="text-xs font-semibold text-slate-500">층수</div>
              <div className="mt-1 text-sm font-medium text-slate-900">
                {(() => {
                  const g = summary?.grnd ? `${summary.grnd}층` : null
                  const u = summary?.ugrnd ? `지하 ${summary.ugrnd}층` : null
                  return [u, g].filter(Boolean).join(' / ') || '-'
                })()}
              </div>
            </div>
            <div className="rounded-xl border bg-white p-4 md:col-span-2">
              <div className="text-xs font-semibold text-slate-500">주택가격(건축물대장, 참고)</div>
              <div className="mt-1 text-sm font-medium text-slate-900">
                {housingPriceRows?.[0]?.hsprc ? `${formatNumber(housingPriceRows[0].hsprc)} 원` : '-'}
              </div>
              <div className="mt-1 text-[11px] text-slate-500">
                기준일: {formatDateDigits(housingPriceRows?.[0]?.stdDay) || '-'}
              </div>
            </div>
          </div>

          <Modal
            open={detailOpen}
            title="건축물대장 상세"
            onClose={() => setDetailOpen(false)}
            headerRight={
              result?.ok ? (
                <button
                  type="button"
                  className="rounded bg-brand-navy text-white px-3 py-1.5 text-sm hover:opacity-90"
                  onClick={() => {
                    setDetailOpen(false)
                    setDesignOpen(true)
                  }}
                >
                  설계의뢰
                </button>
              ) : null
            }
          >
            <div className="grid grid-cols-1 gap-4">
              <BuildingCompactTable title="총괄개요" obj={result?.basisItem} />
              <AmountTable amounts={buildPrefill().fireAmounts} />
              <UnitAreaTable
                dongNm={result?.selectedDongNm}
                hoNm={result?.selectedHoNm}
                rows={result?.unitAreaRows}
              />
              <BuildingCompactTable title="표제부" obj={result?.titleItem} />
              <FloorTable
                rows={floorRows}
                selectedMode={floorSelectMode}
                selectedFloorNoNm={selectedFloorNoNm}
                onSelectMode={setFloorSelectMode}
                onSelectFloor={setSelectedFloorNoNm}
              />
              <HousingPriceTable rows={housingPriceRows} />
              <AtchJibunTable rows={atchJibunRows} />
              <JijiguTable rows={jijiguRows} />
            </div>
          </Modal>

          <Modal open={designOpen} title="화재보험 설계의뢰" onClose={() => setDesignOpen(false)}>
            <div className="space-y-4">
              <div className="rounded border bg-slate-50 p-3 text-sm">
                <div className="font-semibold text-slate-900">대상 주소</div>
                <div className="mt-1 text-slate-700">
                  {roadAddress || jibunAddress || '-'}
                </div>
              </div>

              <div className="rounded border bg-slate-50 p-3 text-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <div className="text-xs font-semibold text-slate-500">고객명</div>
                    <div className="mt-1 font-medium text-slate-900">{String(customerName || '').trim() || '-'}</div>
                  </div>
                  <div className="text-xs text-slate-600">
                    기존고객: <span className="font-semibold">{commonExistingCustomer ? '예' : '아니오'}</span>
                  </div>
                </div>
              </div>

              <div className="rounded border p-3">
                <div className="text-sm font-semibold text-slate-900">요약(설계의뢰 메모로 전달)</div>
                <pre className="mt-2 whitespace-pre-wrap text-xs text-slate-800 bg-slate-50 rounded p-3 overflow-auto">
                  {buildPrefill().commonNote}
                </pre>
              </div>

              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  className="rounded border px-3 py-2 text-sm hover:bg-slate-50"
                  onClick={() => setDesignOpen(false)}
                >
                  취소
                </button>
                <button
                  type="button"
                  className="rounded bg-brand-navy text-white px-3 py-2 text-sm hover:opacity-90 disabled:opacity-50"
                  // disabled={!String(customerName || '').trim()}
                  onClick={() => {
                    const prefill = buildPrefill()
                    const key = `prefill.fire.${Date.now()}.${Math.random().toString(16).slice(2)}`
                    try { sessionStorage.setItem(key, JSON.stringify(prefill)) } catch { }
                    setDesignOpen(false)
                    navigate(`/requests/new?prefillKey=${encodeURIComponent(key)}`)
                  }}
                >
                  설계의뢰 생성하기
                </button>
              </div>
            </div>
          </Modal>
        </div>
      )}
    </div>
  )
}

